import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  ALERT_ICON_TYPE_SUCCESS,
  GOOGLE,
  getGoogleSearchAdGroupType,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import { getIsBlockedActions } from "../../../../actions/getters";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useFormContext } from "react-hook-form";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../../lib/redux";
import { getUniqueID } from "../../../../utils/numbers";
import {
  COPY_VALUES_IN_OTHER_ADS,
  DESCRIPTION_PLACEHOLDER,
  DESCRIPTIONS,
  ADS_CONFIGURATION_DESTINATION_TITLE,
  DESTINATION_URL_PLACEHOLDER,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID,
  READY,
  TITLE_PLACEHOLDER,
  TITLES,
  UTM_PARAMETERS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import TextField from "../../../Form/TextField";
import { waitDelay } from "../../../../utils/date";
import { REGEX_URL } from "../../../../utils/regex";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import CardContainer from "../../../Card/CardContainer";
import CardContainerWithCopy from "../../../Card/CardContainerWithCopy";
import ChipOptional from "../../../Chip/ChipOptional";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";
import { alertIfEmptyCopyValue } from "../../../../utils/copyFormValue";
import {
  commitGeneratedSectionText,
  copySectionValue,
  readStoredSectionFingerprint,
} from "../../../../utils/aiGeneratedForm";
import { AdSelectionGate } from "../../../../pages/NewAdsCampaign/components/AdsCampaignSelectionGate";
import { useRequiredAdSelection } from "../../../../pages/NewAdsCampaign/hooks/requiredAdsCampaignSelection";

const platform = GOOGLE;
const maxTitles = 15;
const maxDescriptions = 4;
const maxDescriptionsDsa = 2;

const AdGoogleForm = () => (
  <AdSelectionGate>
    <AdGoogleFormContent />
  </AdSelectionGate>
);

const AdGoogleFormContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    watch,
    getValues,
    control,
    formState,
    sectionSelected,
    isGeneralDisabledFields,
    setValue,
    trigger,
  } = useFormContext();
  useFocusErrorForm(formState);

  const { adGroup, adID, BASIC_PATH, BASIC_PATH_AD_GROUP } =
    useRequiredAdSelection();
  const errors = formState.errors;
  const { t } = useTranslationApp();

  const platformData = watch(`platformsData.${platform}`);
  const isIncompatibleCampaign = platformData?.isIncompatible;

  const isIncompatibleAdGroup = adGroup?.isIncompatible || false;
  const isDsa =
    getGoogleSearchAdGroupType(adGroup) ===
    ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION;
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [{ value: "" }];
  const isIncompatibleAd = getValues(`${BASIC_PATH}.isIncompatible`) || false;
  const adStatus = watch(`${BASIC_PATH}.status`) || ADS_CAMPAIGN_STATUS_DRAFT;
  const isAdCreated = adStatus !== ADS_CAMPAIGN_STATUS_DRAFT;

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAdGroup ||
    isIncompatibleAd;

  const isDescriptionsDisabled = isDisabledFields || (isDsa && isAdCreated);

  const objective = platformData?.objective;

  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();

  const onGenerateText = async ({ type, index }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );

    if (isBlocked) {
      return;
    }

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");

    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const response = await dispatch(
      generateAdsCampaignDescription({
        campaignID: getValues("id"),
        objective,
        platform,
        keywords: getValues(`${BASIC_PATH_AD_GROUP}.keywords`) || [],
        campaignContext: getValues("context") || "",
        destinationUrl:
          getValues(`${BASIC_PATH}.destinationUrls`)?.[0]?.value || "",
        language: getValues(`language`),
        type,
        currentTexts: textsWithCurrentFirst,
        entityType: "ad",
        currentFingerprint: readStoredSectionFingerprint({
          getValues,
          basePath: BASIC_PATH,
          section: type,
        }),
      })
    );

    commitGeneratedSectionText({
      getValues,
      setValue,
      trigger,
      basePath: BASIC_PATH,
      section: type,
      index,
      platform,
      entityType: "ad",
      response,
    });
  };

  const onAddText = async ({ type }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    setValue(`${BASIC_PATH}.${type}`, [
      ...currentTexts,
      {
        value: "",
        id: getUniqueID(),
      },
    ]);
  };
  const onRemoveText = async ({ type, index }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`);
    setValue(
      `${BASIC_PATH}.${type}`,
      currentTexts.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    trigger(`${BASIC_PATH}.${type}`);
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    if (alertIfEmptyCopyValue({ value, t })) return;
    const adsOnAdgroup = getValues(`${BASIC_PATH_AD_GROUP}.ads`) || [];

    adsOnAdgroup.forEach((ad, currentIndexAd) => {
      if (ad.id === adID) return;

      copySectionValue({
        setValue,
        getValues,
        sourceBasePath: BASIC_PATH,
        section: type,
        platform,
        entityType: "ad",
        path: `${BASIC_PATH_AD_GROUP}.ads.[${currentIndexAd}].${type}`,
        value,
      });
    });
    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  //To reset values component when change selectedLevel
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  if (isLoading) return null;

  const activeMaxDescriptions = isDsa ? maxDescriptionsDsa : maxDescriptions;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          height: "min-content",
          width: "100%",
        }}
      >
        {!isDsa && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
                  </Typography>
                </Box>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="body1">
                      {t(DESTINATION_URL_PLACEHOLDER)}
                    </Typography>
                    <TextField
                      name={`${BASIC_PATH}.destinationUrls.[0].value`}
                      variant="filled"
                      fullWidth
                      disabled={isDisabledFields}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: false,
                        },
                        validate: (value) => {
                          if (!value) {
                            return t(FIELD_REQUIRED);
                          }
                          if (value && !REGEX_URL.test(encodeURI(value))) {
                            return t(FIELD_URL_VALID);
                          }
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "destinationUrls" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {t(UTM_PARAMETERS_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <UtmParametersContainer
                      name={`${BASIC_PATH}.utmParameters`}
                      control={control}
                      errors={errors}
                      disabled={isDisabledFields}
                      platform={platform}
                      googleUtmEntityFormPath={BASIC_PATH}
                      onCopyValuesInOtherAds={() =>
                        onCopyValuesInOtherAds({ type: "utmParameters" })
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {!isDsa && (
          <Grid item xs={12} sm={6} id={`${BASIC_PATH}.titles`}>
            <CardContainerWithCopy
              title={t(TITLES)}
              disabled={isDisabledFields}
              hasCopyValue={true}
              onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
              id={`${BASIC_PATH}.titles`}
              copyText={COPY_VALUES_IN_OTHER_ADS}
            >
              {titles?.map((title, index) => (
                <TextFieldWithActions
                  key={title.id}
                  type="titles"
                  labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                  name={`${BASIC_PATH}.titles.[${index}].value`}
                  id={`${BASIC_PATH}.titles.[${index}].value`}
                  disabled={isDisabledFields}
                  showAddIcon={
                    index === titles.length - 1 && index < maxTitles - 1
                  }
                  showDeleteIcon={index !== 0}
                  platform={platform}
                  onAdd={() => onAddText({ type: "titles" })}
                  onRemove={() => onRemoveText({ type: "titles", index })}
                  maxLength={ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]}
                  onGenerate={async () => {
                    await onGenerateText({ type: "titles", index });
                  }}
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: true,
                      message: t(FIELD_REQUIRED),
                    },
                    validate: (value) => {
                      if (
                        value.length >
                        ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]
                      ) {
                        return t(FIELD_MAX_LENGTH, {
                          value:
                            ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform],
                        });
                      }

                      const allTitles = titles.map((item) =>
                        (item.value || "").trim().toLowerCase()
                      );
                      const timesRepeated = allTitles.filter(
                        (text) => text === value.trim().toLowerCase()
                      ).length;
                      if (timesRepeated > 1) {
                        return t(FIELD_TEXT_REPEATED);
                      }
                      return true;
                    },
                  }}
                />
              ))}
            </CardContainerWithCopy>
          </Grid>
        )}

        <Grid
          item
          xs={12}
          sm={isDsa ? 12 : 6}
          id={`${BASIC_PATH}.descriptions`}
        >
          <CardContainerWithCopy
            title={t(DESCRIPTIONS)}
            disabled={isDescriptionsDisabled}
            hasCopyValue={true}
            onCopyCallback={() =>
              onCopyValuesInOtherAds({ type: "descriptions" })
            }
            id={`${BASIC_PATH}.descriptions`}
            copyText={COPY_VALUES_IN_OTHER_ADS}
          >
            {descriptions?.map((description, index) => (
              <TextFieldWithActions
                key={description.id}
                labelTitle={t(DESCRIPTION_PLACEHOLDER) + " " + (index + 1)}
                type="descriptions"
                name={`${BASIC_PATH}.descriptions.[${index}].value`}
                id={`${BASIC_PATH}.descriptions.[${index}].value`}
                disabled={isDescriptionsDisabled}
                platform={platform}
                showAddIcon={
                  index === descriptions.length - 1 &&
                  index < activeMaxDescriptions - 1
                }
                showDeleteIcon={index !== 0}
                maxLength={
                  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform]
                }
                onAdd={() => onAddText({ type: "descriptions" })}
                onRemove={() => onRemoveText({ type: "descriptions", index })}
                onGenerate={async () => {
                  await onGenerateText({ type: "descriptions", index });
                }}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                  validate: (value) => {
                    if (
                      value.length >
                      ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform]
                    ) {
                      return t(FIELD_MAX_LENGTH, {
                        value:
                          ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[
                            platform
                          ],
                      });
                    }
                    const allDescriptions = descriptions.map((item) =>
                      (item.value || "").trim().toLowerCase()
                    );
                    const timesRepeated = allDescriptions.filter(
                      (text) => text === value.trim().toLowerCase()
                    ).length;
                    if (timesRepeated > 1) {
                      return t(FIELD_TEXT_REPEATED);
                    }
                    return true;
                  },
                }}
              />
            ))}
          </CardContainerWithCopy>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdGoogleForm;
